import React from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import {
  Container,
  Columns,
  Column,
  Dropdown,
  Select,
  ButtonGroup,
} from '@resurs/midas-react'

import { DataTable } from '../../../midasComponents'

import Page from '../../../internal/Page'
import PageActionBar from '../../../internal/PageActionBar'

import pageableTable from '../../../higher-order/pageable-table'
import searchableTable from '../../../higher-order/searchable-table'

import { getText } from '../../../internal/TextLabels'
import { RoleLabels } from '../../../models/roles'
import { sendAddSingleUserEvent } from '../../../util/ga-helpers'
import DropdownButton from './DropdownButton'
import { Features, isEnabled } from '../../../models/features'

const UsersUI = props => {
  const filters = [
    {
      key: 'active',
      value: 'active',
      valueLabel: getText('user.list.filter.active'),
    },
    {
      key: 'inactive',
      value: 'inactive',
      valueLabel: getText('user.list.filter.inactive'),
    },
  ]

  const tableHeaders = [
    {
      id: 'username',
      label: getText('user.list.columnHeader.userName'),
    },
    {
      id: 'lastName',
      label: getText('user.list.columnHeader.name'),
    },
    {
      id: 'role',
      label: getText('user.list.columnHeader.role'),
    },
  ]

  const roles = new RoleLabels()

  const onSearch = searchTerm => {
    const validRoles = props.users.validRoles
    const matchedRole = []
    for (let i = 0; i < validRoles.length; i++) {
      if (
        searchTerm !== '' &&
        roles.labels[validRoles[i].name]
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ) {
        matchedRole.push(validRoles[i].name)
      }
    }
    props.onSearch(searchTerm, matchedRole)
  }

  return (
    <Container>
      <Columns>
        <Column col2 colXl0 />
        <Column col8 colXl12>
          <Page title={getText('user.userList')}>
            <PageActionBar
              items={
                <div>
                  <Select
                    id="user-filter"
                    value={props.activeFilter}
                    onChange={e => props.onToggleActive(e)}
                    values={filters}
                  />
                </div>
              }
              actions={
                <ButtonGroup spacing>
                  {isEnabled(Features.USER_EXPORT) && ( // TODO: MP-304 remove once production stable
                    <Dropdown
                      type="primary"
                      size="small"
                      text={getText('user.export.button.title')}
                      values={[
                        <DropdownButton
                          key="export-store-users"
                          title={getText('user.export.button.store.one')}
                          onClick={props.onExportStoreUsers}
                        />,
                        props.isSingleOwnerOrFranchise && (
                          <DropdownButton
                            key="export-representative-users"
                            title={getText('user.export.button.store.many')}
                            onClick={props.onExportRepresentativeUsers}
                          />
                        ),
                      ].filter(value => !!value)}
                    />
                  )}
                  <Dropdown
                    type="primary"
                    size="small"
                    text={getText('user.addUser.title')}
                    values={[
                      <NavLink
                        to="users/add-user"
                        onClick={() => {
                          sendAddSingleUserEvent('Start')
                          props.onAddUser()
                        }}
                      >
                        {getText('user.addUser.one')}
                      </NavLink>,
                      <NavLink to="users/import">
                        {getText('user.addUser.many')}
                      </NavLink>,
                    ]}
                    dataCy="add-user-dropdown"
                  />
                </ButtonGroup>
              }
            />
            <DataTable
              striped
              hover
              fixed
              search
              searchTitle={getText('default.search')}
              rowsPerPageTitle={getText('default.rowsPerPage')}
              disableRowActions
              searchTerm={props.searchTerm}
              placeholder={getText('default.search')}
              onSearch={term => onSearch(term)}
              pagination={{
                currentPage: props.pagination.pageNumber + 1,
                totalPages: props.pagination.totalPages,
                rowsPerPage: props.pagination.size,
              }}
              lastSort={{
                headerName: props.lastSort.headerName,
                direction: props.lastSort.direction,
              }}
              onNext={() => props.onNext()}
              onPrev={() => props.onPrev()}
              onRowsPerPageChange={size => props.onChangeSize(size)}
              onSort={(currentHeaderId, direction) =>
                props.onChangeSortPriority(currentHeaderId, direction)
              }
              actionModel={{
                label: getText('default.handleSelectedRows'),
                default: 'default',
                loading: true,
                options: [
                  {
                    value: 'default',
                    label: getText('default.choose'),
                    disabled: true,
                  },
                  {
                    value: 'delete',
                    label: getText('default.delete'),
                  },
                ],
              }}
              headers={tableHeaders}
            >
              <tbody>
                {props.users.byId.map(userId => (
                  <tr
                    key={userId}
                    onClick={() => props.onNavigateToDetailView(userId)}
                  >
                    <td>{props.users.byHash[userId].username}</td>
                    <td>
                      {props.users.byHash[userId].firstName}
                      &nbsp;
                      {props.users.byHash[userId].lastName}
                    </td>
                    <td>{roles.labels[props.users.byHash[userId].role]}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Page>
        </Column>
      </Columns>
    </Container>
  )
}

UsersUI.propTypes = {
  users: PropTypes.object.isRequired,
  pagination: PropTypes.object.isRequired,
  activeFilter: PropTypes.string.isRequired,
  lastSort: PropTypes.shape({
    headerName: PropTypes.string,
    direction: PropTypes.string,
  }),
  isSingleOwnerOrFranchise: PropTypes.bool.isRequired,
  onToggleActive: PropTypes.func.isRequired,
  onNext: PropTypes.func,
  onPrev: PropTypes.func,
  onChangeSize: PropTypes.func,
  onChangeSortPriority: PropTypes.func,
  onSearch: PropTypes.func,
  searchTerm: PropTypes.string,
  onNavigateToDetailView: PropTypes.func,
  onAddUser: PropTypes.func,
  onExportStoreUsers: PropTypes.func.isRequired,
  onExportRepresentativeUsers: PropTypes.func.isRequired,
}

UsersUI.defaultProps = {
  searchTerm: '',
  lastSort: {
    headerName: '',
    direction: '',
  },
  onNext: () => {},
  onPrev: () => {},
  onChangeSize: () => {},
  onChangeSortPriority: () => {},
  onSearch: () => {},
  onNavigateToDetailView: () => {},
  onAddUser: () => {},
}

export default searchableTable(pageableTable(UsersUI))
